import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Heart, LogOut, User, LayoutDashboard, HeartHandshake } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Volunteer', path: '/volunteer' },
    { name: 'Need Help', path: '/help' },
    { name: 'Events', path: '/events' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isHome = location.pathname === '/';

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled || !isHome ? 'bg-white/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
      <div className="container-custom">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden transition-all ${scrolled || !isHome ? 'shadow-sm' : ''}`}>
              <img src="/logo.jpeg" alt="Sevasaredham Logo" className="w-full h-full object-cover" />
            </div>
            <span className={`text-2xl font-heading font-bold transition-colors ${scrolled || !isHome ? 'text-gray-900' : 'text-white'}`}>
              Sevasaredham
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-accent relative group ${location.pathname === link.path
                  ? 'text-accent font-semibold'
                  : (scrolled || !isHome) ? 'text-gray-600' : 'text-white/90'
                  }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full ${location.pathname === link.path ? 'w-full' : ''}`}></span>
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center space-x-4">
            {user ? (
              <>
                <Link
                  to={isAdmin ? '/admin' : '/dashboard'}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${scrolled || !isHome
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                    }`}
                >
                  {isAdmin ? <LayoutDashboard size={18} /> : <User size={18} />}
                  <span className="font-medium text-sm">{isAdmin ? 'Admin' : 'Dashboard'}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${scrolled || !isHome
                    ? 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                    : 'text-white hover:text-red-300 hover:bg-white/10'
                    }`}
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className={`px-4 py-2 text-sm font-medium transition-colors mr-2 ${scrolled || !isHome ? 'text-gray-700 hover:text-primary' : 'text-white hover:text-accent'
                  }`}
              >
                Login
              </Link>
            )}
            <Link to="/donate" className={`btn ${scrolled || !isHome ? 'btn-primary' : 'btn-accent'} text-sm px-6 py-2.5 shadow-none`}>
              Donate Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${scrolled || !isHome ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t overflow-hidden shadow-xl"
          >
            <div className="container-custom py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg transition-colors ${location.pathname === link.path
                    ? 'bg-primary/5 text-primary font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-gray-100 my-2 pt-2">
                {user ? (
                  <>
                    <Link
                      to={isAdmin ? '/admin' : '/dashboard'}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-2 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {isAdmin ? <LayoutDashboard size={20} /> : <User size={20} />}
                      <span>{isAdmin ? 'Admin Dashboard' : 'Dashboard'}</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-2 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <LogOut size={20} />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Login
                  </Link>
                )}
              </div>
              <div className="p-4">
                <Link
                  to="/donate"
                  onClick={() => setIsOpen(false)}
                  className="block text-center btn btn-primary w-full"
                >
                  Donate Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;